import { Injectable, OnDestroy } from '@angular/core';
import { Observable, Subject, Subscription, interval } from 'rxjs';
import * as moment from 'moment';

import { AppConsts } from '@shared/commonHelper/AppConsts';
import { AppSessionService } from '@shared/common/session/app-session.service';
import { DayRolloverEvent, DayRolloverTrigger } from './day-rollover.model';

/**
 * Detects that the user's WORK DATE has changed while the tab stayed open, and emits once so
 * consumers can re-seed themselves — replacing the check-in popup's full `location.reload()`.
 *
 * WHY TWO TIERS
 * The FE cannot predict the rollover instant. `companyTzWorkDate` is derived server-side from the
 * user's SHIFT (FN_CompanyTz_User_WorkDate -> FN_User_Shift_DateTime), not from calendar midnight, so
 * a night-shift user rolls over mid-calendar-day. Therefore:
 *
 *   Tier 1 (cheap, local, no network) raises a CANDIDATE when the company-tz calendar date changes,
 *          when the tab regains focus/visibility, or at the known next-shift boundary.
 *   Tier 2 (authoritative) calls AppSessionService.init() -> Session/GetCurrentLoginInformations and
 *          emits ONLY if the returned companyTzWorkDate actually differs. The server decides; the FE
 *          never guesses the new date.
 *
 * WHY NOT JUST POLL init() EVERY MINUTE
 * Two reasons, the second of which is the important one:
 *   1. COST — GetCurrentLoginInformations also runs CreateUserCheckInOut, default-break creation, and
 *      broadcasts `getUserInOut` to Clients.All (SessionAppService.cs:371). Cheap for one tab, not cheap
 *      tenant-wide.
 *   2. SIDE EFFECT — it is NOT a pure read. It also runs Vf_Get_Checkin_User_Redirect, which (despite
 *      the "Get" name) INSERTS a child CheckIn row when the latest event for the current work date is a
 *      CheckOut. An unconditional refresh can therefore silently check a deliberately-checked-out user
 *      back in. So every trigger below must have positive reason to believe the work date MOVED — never
 *      "it's been a while, refresh anyway". See onRegainedAttention() for the full note.
 *
 * IDEMPOTENCY
 * Vf_Create_User_CheckInOut only inserts a CheckIn row when the freshly computed WorkDate has no
 * un-deleted CheckIn row (lines 115-121), and task carry-forward
 * (Vf_Move_Previous_Task_To_CurrentWorkDate) runs inside that branch. So repeated Tier-2 checks on the
 * same work date are a no-op server-side, and carry-forward runs at most once per work date.
 *
 * Gated by AppConsts.dayRolloverV1FeatureEnabled — when false, start() is a no-op and this service
 * creates no timers, listeners or requests.
 *
 * See CHECKIN_ELIMINATION_PLAN.md §3 and CHECKIN_REMOVAL_TIERS.md (T2/T3).
 */
@Injectable()
export class DayRolloverService implements OnDestroy {

    private static readonly HeartbeatMs = 60 * 1000;
    /** Backoff schedule after a failed authoritative check, then fall back to the heartbeat cadence. */
    private static readonly RetryDelaysMs = [30 * 1000, 2 * 60 * 1000, 5 * 60 * 1000];

    private readonly _dayRolledOver = new Subject<DayRolloverEvent>();

    private _started = false;
    private _checking = false;
    private _retryIndex = 0;
    private _lastCheckMs = 0;

    /** Server-authoritative work date the app is currently showing, YYYY-MM-DD. */
    private _knownWorkDate: string = null;
    /** Company-tz calendar date last observed locally, YYYY-MM-DD. Tier-1 comparison basis only. */
    private _knownCompanyDate: string = null;

    private _heartbeat: Subscription = null;
    private _shiftTimer: number = null;
    private _retryTimer: number = null;
    private _onVisibility: () => void = null;
    private _onFocus: () => void = null;

    constructor(private _appSession: AppSessionService) { }

    /** Emits once per genuine work-date change. Never emits on a false-positive candidate. */
    get dayRolledOver$(): Observable<DayRolloverEvent> {
        return this._dayRolledOver.asObservable();
    }

    /** The work date consumers should currently be rendering, or null before start(). */
    get knownWorkDate(): string {
        return this._knownWorkDate;
    }

    /**
     * Begin watching. Safe to call more than once — only the first call takes effect. No-op when the
     * feature flag is off or when there is no session yet (e.g. the login page).
     */
    start(): void {
        if (!AppConsts.dayRolloverV1FeatureEnabled || this._started) {
            return;
        }
        if (!this._appSession.user) {
            return;
        }

        this._started = true;
        this._knownWorkDate = this.readWorkDate();
        this._knownCompanyDate = this.companyDateNow();
        this._lastCheckMs = Date.now();

        this._heartbeat = interval(DayRolloverService.HeartbeatMs).subscribe(() => this.onHeartbeat());

        this._onVisibility = () => {
            if (document.visibilityState === 'visible') {
                this.onRegainedAttention();
            }
        };
        this._onFocus = () => this.onRegainedAttention();
        document.addEventListener('visibilitychange', this._onVisibility);
        window.addEventListener('focus', this._onFocus);

        this.scheduleShiftBoundaryCheck();
        this.installDevHook();
    }

    ngOnDestroy(): void {
        this.stop();
    }

    /**
     * Non-production testing hook. Exposes `window.__vfDayRollover`.
     *
     * WHY THIS IS NEEDED. A rollover only emits when the SERVER's `companyTzWorkDate` changes, and that
     * value is derived purely from the shift (`FN_CompanyTz_User_WorkDate` returns `WorkDate` from the
     * `ShiftStartDt` CASE — the check-in table only feeds a separate `CheckInWorkDate` column that
     * `Vf_Get_CompanyTzWorkDate` does not select). So there is NO way to move it from the outside short of
     * real elapsed time, editing `TrShifts`, or changing the tenant timezone.
     *
     * In particular, **backdating `TrUserCheckInOuts.WorkDate` does NOT trigger a rollover** — it only
     * decides whether a new CheckIn row is inserted. That simulation tests carry-forward, not this service.
     *
     * `simulate()` therefore fakes the *previously known* date and runs a real Tier-2 check, which
     * exercises the genuine emit path and every consumer handler with no clock, schema or DB change.
     */
    private installDevHook(): void {
        if (!this.isLocalDevHost()) {
            return;
        }
        window['__vfDayRollover'] = {
            /** Current internal state — no side effects. */
            status: (): object => ({
                started: this._started,
                checking: this._checking,
                knownWorkDate: this._knownWorkDate,
                knownCompanyDate: this._knownCompanyDate,
                companyDateNow: this.companyDateNow(),
                serverWorkDate: this.readWorkDate(),
                secondsSinceLastCheck: Math.round((Date.now() - this._lastCheckMs) / 1000)
            }),
            /** Real Tier-2 check. Emits only if the server's work date genuinely moved (usually: nothing). */
            check: (): void => this.runAuthoritativeCheck('manual'),
            /** Pretend we were showing yesterday, then run a real check -> forces one genuine emit. */
            simulate: (): string => {
                const current = this.readWorkDate();
                const faked = current
                    ? moment(current, 'YYYY-MM-DD').subtract(1, 'day').format('YYYY-MM-DD')
                    : null;
                this._knownWorkDate = faked;
                this.runAuthoritativeCheck('manual');
                return faked;
            }
        };
    }

    stop(): void {
        this._started = false;
        if (this._heartbeat) { this._heartbeat.unsubscribe(); this._heartbeat = null; }
        if (this._shiftTimer !== null) { window.clearTimeout(this._shiftTimer); this._shiftTimer = null; }
        if (this._retryTimer !== null) { window.clearTimeout(this._retryTimer); this._retryTimer = null; }
        if (this._onVisibility) { document.removeEventListener('visibilitychange', this._onVisibility); this._onVisibility = null; }
        if (this._onFocus) { window.removeEventListener('focus', this._onFocus); this._onFocus = null; }
        if (this.isLocalDevHost()) { delete window['__vfDayRollover']; }
        this._dayRolledOver.complete();
    }

    // ---- Tier 1: cheap local candidates -----------------------------------------------------

    private onHeartbeat(): void {
        const companyDate = this.companyDateNow();
        if (companyDate && companyDate !== this._knownCompanyDate) {
            this._knownCompanyDate = companyDate;
            this.runAuthoritativeCheck('heartbeat');
        }
    }

    /**
     * Focus / visibility regained. Timers do not fire while a machine sleeps, and Chrome throttles
     * background-tab timers hard, so this — not the heartbeat — is the primary path for a tab that
     * was asleep across the boundary.
     *
     * ⚠️ DELIBERATELY fires ONLY when the company-tz date actually changed. Do NOT add a
     * "it's been a while, check anyway" fallback here — an earlier revision had one (4h) and it was
     * removed, because calling init() has a SIDE EFFECT on check-in state:
     *
     *   GetCurrentLoginInformations also runs Vf_Get_Checkin_User_Redirect, which — despite the "Get"
     *   name — INSERTS a child CheckIn row whenever the latest event for the current work date is a
     *   CheckOut (`IF @CheckOutType = 2`). So an unconditional refresh can silently check a
     *   deliberately-checked-out user back in.
     *
     * Rule of thumb: only call init() when we have positive reason to believe the work date moved. A
     * trigger that is right about that is safe — Vf_Create_User_CheckInOut inserts the parent CheckIn
     * first, so the latest row is then a CheckIn and no spurious child is added.
     */
    private onRegainedAttention(): void {
        const companyDate = this.companyDateNow();
        if (companyDate && companyDate !== this._knownCompanyDate) {
            this._knownCompanyDate = companyDate;
            this.runAuthoritativeCheck('visibility');
        }
    }

    /**
     * Night shifts roll the work date at the shift boundary, not at company midnight, and the FE
     * cannot derive that instant. `userCheckin.nextShiftStartTime` already carries it, so schedule a
     * single check just after it. Defensive throughout: any unparseable/past value simply leaves the
     * heartbeat + visibility paths to cover it.
     *
     * This is the one trigger that BETS the work date moved rather than observing it locally — which is
     * the whole point, since a night shift's rollover is invisible to a calendar-date comparison. If the
     * bet is wrong the cost is bounded: at most one spurious re-check-in, at most once per shift
     * boundary (see the side-effect note on onRegainedAttention). Do not generalise this into a
     * recurring timer.
     */
    private scheduleShiftBoundaryCheck(): void {
        const checkin = this._appSession.userCheckin;
        const raw = checkin ? checkin.nextShiftStartTime : null;
        if (!raw) {
            return;
        }

        const offset = this.normalizeOffset(this._appSession.user ? this._appSession.user.userTimeZoneOffset : null);
        // nextShiftStartTime is the shift start in the USER's local time with no zone marker, so pin
        // the user's offset onto it to get a real instant.
        const shiftStart = offset
            ? moment.parseZone(moment(String(raw)).format('YYYY-MM-DDTHH:mm:ss') + offset)
            : moment(String(raw));
        if (!shiftStart.isValid()) {
            return;
        }

        // +30s so the server has definitely crossed the boundary its own CASE expression tests.
        const delay = shiftStart.valueOf() + 30 * 1000 - Date.now();
        if (delay <= 0 || delay > 24 * 60 * 60 * 1000) {
            return;
        }
        this._shiftTimer = window.setTimeout(() => {
            this._shiftTimer = null;
            this.runAuthoritativeCheck('shift-boundary');
        }, delay);
    }

    // ---- Tier 2: authoritative confirmation -------------------------------------------------

    /**
     * Re-read the session and emit only if the server's work date moved. Comparing against the STORED
     * date (not against elapsed days) means a tab asleep for three days emits exactly one rollover.
     */
    private runAuthoritativeCheck(trigger: DayRolloverTrigger): void {
        if (this._checking) {
            return; // a candidate arriving mid-check is dropped; the in-flight check covers it
        }
        this._checking = true;

        this._appSession.init().then(() => {
            this._checking = false;
            this._lastCheckMs = Date.now();
            this._retryIndex = 0;

            const current = this.readWorkDate();
            if (!current) {
                return;
            }
            const previous = this._knownWorkDate;
            this._knownWorkDate = current;
            this._knownCompanyDate = this.companyDateNow() || this._knownCompanyDate;
            // Re-arm for the next shift, which init() has just refreshed.
            if (this._shiftTimer === null) {
                this.scheduleShiftBoundaryCheck();
            }

            if (previous && current !== previous) {
                // Rollover is otherwise completely silent, which makes "is it firing?" unanswerable on a
                // real user's tab — and popup suppression can only be observed in prod, where the
                // CheckinReminderWorker actually runs. One line, no behaviour, greppable from a console.
                console.info(`[DayRollover] ${previous} -> ${current} (${trigger})`);
                this._dayRolledOver.next({ previousWorkDate: previous, currentWorkDate: current, trigger });
            }
        }).catch(() => {
            // Offline / 401 / 5xx. Keep the previously known date and DO NOT emit — a half-refreshed
            // My Day (new header, old meetings) is worse than a stale-but-consistent one.
            this._checking = false;
            this.scheduleRetry(trigger);
        });
    }

    private scheduleRetry(trigger: DayRolloverTrigger): void {
        if (this._retryTimer !== null || !this._started) {
            return;
        }
        const delays = DayRolloverService.RetryDelaysMs;
        if (this._retryIndex >= delays.length) {
            this._retryIndex = 0;
            return; // give up on the backoff ladder; the heartbeat/visibility paths still apply
        }
        const delay = delays[this._retryIndex];
        this._retryIndex += 1;
        this._retryTimer = window.setTimeout(() => {
            this._retryTimer = null;
            this.runAuthoritativeCheck(trigger);
        }, delay);
    }

    // ---- helpers ----------------------------------------------------------------------------

    /** The server's current work date as YYYY-MM-DD, or null when the session has none. */
    private readWorkDate(): string {
        const user = this._appSession.user;
        if (!user || !user.companyTzWorkDate) {
            return null;
        }
        const m = moment.parseZone(String(user.companyTzWorkDate));
        return m.isValid() ? m.format('YYYY-MM-DD') : null;
    }

    /**
     * Live "today" in the COMPANY timezone, YYYY-MM-DD — the Tier-1 comparison basis. Deliberately
     * NOT the machine's local date: on a real tenant these differ. (Observed on vf-app-dev: company
     * UTC+03:00 vs machine UTC-04:00, so company midnight arrives 7h before the machine's — a
     * machine-date watcher would fire ~7h late.) Returns null when no offset is known, in which case
     * Tier 1 relies on focus/visibility and the shift boundary instead of guessing.
     */
    private companyDateNow(): string {
        const user = this._appSession.user;
        if (!user) {
            return null;
        }
        const offset = this.normalizeOffset(user.companyTimeZoneOffset)
            || this.normalizeOffset(user.userTimeZoneOffset);
        if (!offset) {
            return null;
        }
        return moment().utcOffset(offset).format('YYYY-MM-DD');
    }

    /**
     * True only when the app is served from a developer machine.
     *
     * ⚠️ Do NOT use `environment.production` for this in THIS repo — it is `true` in
     * `src/environments/environment.ts`, which is the file `ng serve` actually compiles (the base
     * `build` target declares no fileReplacements). Every environment file except `environment.hmr.ts`
     * sets `production: true`, so that flag says nothing about dev vs prod here. Hostname does.
     */
    private isLocalDevHost(): boolean {
        const host = window.location.hostname;
        return host === 'localhost' || host === '127.0.0.1' || host === '[::1]';
    }

    /**
     * Backend offsets arrive as TimeSpan strings ("-04:00:00", "05:30:00" — TimeSpan never emits "+")
     * or already-normalized ("+03:00"). Reduce to the ±HH:MM that moment accepts. Mirrors
     * mine.component.resolveUserTzOffset().
     */
    private normalizeOffset(raw: string): string {
        const value = raw ? String(raw).trim() : '';
        if (!value) {
            return null;
        }
        const match = value.match(/^([+-]?)(\d{1,2}):(\d{2})(?::\d{2})?$/);
        if (!match) {
            return null;
        }
        const sign = match[1] || '+';
        return `${sign}${match[2].padStart(2, '0')}:${match[3]}`;
    }
}
